import { httpClient } from './http-client';
import { API_CONFIG, STORAGE_KEYS } from './api.config';
import type { ApiResponse } from './api.types';

export interface ArticuloAyudaDto {
  id: string;
  categoria: string;
  icono: string;
  titulo: string;
  snippet: string;
  pasos: string[];
  mediaTipo?: string;
  mediaUrl?: string;
  mediaDuracion?: string;
  orden: number;
  esActivo: boolean;
  usuarioId?: string;
  usuarioNombre?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ArticuloAyudaInputDto {
  categoria: string;
  icono: string;
  titulo: string;
  snippet: string;
  pasos: string[];
  mediaTipo?: string;
  mediaDuracion?: string;
  orden: number;
  esActivo: boolean;
}

export interface FeedbackAyudaInputDto {
  articuloId: string;
  esUtil: boolean;
}

export interface FeedbackAyudaDto {
  id: string;
  articuloId: string;
  empresaId?: string;
  usuarioId?: string;
  esUtil: boolean;
  createdAt: string;
  updatedAt: string;
}

function buildApiUrl(endpoint: string): string {
  const url = new URL(
    `${API_CONFIG.BASE_URL}/api/${API_CONFIG.API_VERSION}${endpoint}`,
    window.location.origin,
  );
  return url.toString();
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const empresaId = localStorage.getItem(STORAGE_KEYS.EMPRESA_ID);
  if (empresaId) headers['X-Empresa-Id'] = empresaId;
  const establecimientoId = localStorage.getItem(STORAGE_KEYS.ESTABLECIMIENTO_ID);
  if (establecimientoId) headers['X-Establecimiento-Id'] = establecimientoId;
  return headers;
}

async function parseApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const json = await response.json();
  if (!response.ok) {
    const msg = json?.mensaje || json?.message || json?.error || `Error HTTP ${response.status}`;
    throw new Error(msg);
  }
  const data = ('exito' in json && 'data' in json) ? json.data : json;
  return { data: data as T, status: response.status, ok: true };
}

export const centroAyudaService = {
  getArticulos: (soloActivos = true) =>
    httpClient.get<ArticuloAyudaDto[]>('/centro-ayuda/articulos', { soloActivos }),

  getArticuloById: (id: string) =>
    httpClient.get<ArticuloAyudaDto>(`/centro-ayuda/articulos/${id}`),

  crearArticulo: (input: ArticuloAyudaInputDto) =>
    httpClient.post<ArticuloAyudaDto>('/centro-ayuda/articulos', input),

  actualizarArticulo: (id: string, input: ArticuloAyudaInputDto) =>
    httpClient.put<ArticuloAyudaDto>(`/centro-ayuda/articulos/${id}`, input),

  eliminarArticulo: (id: string) =>
    httpClient.delete<object>(`/centro-ayuda/articulos/${id}`),

  subirMedia: async (
    id: string,
    file: File,
    mediaTipo: 'video' | 'foto' | 'gif',
    mediaDuracion?: string,
  ): Promise<ApiResponse<ArticuloAyudaDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    formData.append('mediaTipo', mediaTipo);
    if (mediaDuracion) formData.append('mediaDuracion', mediaDuracion);
    const response = await fetch(buildApiUrl(`/centro-ayuda/articulos/${id}/media`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArticuloAyudaDto>(response);
  },

  enviarFeedback: (input: FeedbackAyudaInputDto) =>
    httpClient.post<object>('/centro-ayuda/feedback', input),

  getFeedback: () => httpClient.get<FeedbackAyudaDto[]>('/centro-ayuda/feedback'),

  descargarReporte: () => httpClient.obtenerBlob('/reportes/centro-ayuda/excel'),
};
